import React, {
	useMemo,
	useState,
	useEffect,
} from 'react';
import {
	Card,
	Flex,
} from 'antd';
import {
	useHistory,
	useLocation,
} from 'react-router';
import { CargoContractSides } from '@shared/utils/constants';
import type { CargoProps } from '@api/models/cargo';
import type { CargoPortProps } from '@api/models/cargo-port';
import type { PortCallActionProps } from '@api/models/port-call-action';
import type { CargoStats } from '@api/features/laytime/getCargosAndLaytimes';
import type { TimesheetProps } from '@api/models/timesheet';
import type { ExclusionGroupProps } from '@api/models/exclusion-group';
import type { ExclusionItemProps } from '@api/models/exclusion-item';
import type { TimesheetRowProps } from '@api/models/timesheet-row';
import Claims from '@client/screens/cargos/components/ClaimsModal/Claims';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import { useVoyage } from '../components/VoyageProvider/VoyageProvider';
import LaytimeInfo from './LaytimeInfo';

export type SelectedCargo = CargoProps & {
	CargoPorts: Array<CargoPortProps & {
		ExclusionGroup: ExclusionGroupProps & {
			ExclusionItems: Array<ExclusionItemProps>;
		};
		actions: Array<PortCallActionProps>;
	}>;
	Timesheets: Array<TimesheetProps & {
		TimesheetRows: Array<TimesheetRowProps>;
	}>;
	stats: CargoStats;
};

const ClaimsTab = () => {
	const history = useHistory();
	const location = useLocation();

	const {
		cargos,
	} = useVoyage();

	const [selectedCargoId, setSelectedCargoId] = useState<string | undefined>();
	const [selectedCargoPortId, setSelectedCargoPortId] = useState<string | undefined>();

	const sortCargos = (a: CargoProps, b: CargoProps) => {
		if (a.contractSide === b.contractSide) {
			return a.id - b.id;
		}

		return a.contractSide === CargoContractSides.VC_OUT ? -1 : 1;
	};

	useEffect(() => {
		if (selectedCargoId && selectedCargoPortId) {
			return;
		}

		const params = new URLSearchParams(location.hash);
		const asArray = Array.from(params.entries());
		const entry = asArray.find((c) => c[0].includes('cargoId'));
		const cargoId = entry?.[1];

		if (cargoId && cargos && (!selectedCargoId || !selectedCargoPortId)) {
			const foundCargo = cargos.find((c) => c.id.toString() === cargoId);

			if (foundCargo) {
				setSelectedCargoId(foundCargo.id.toString());

				const cargoPortId = params.get('cargoPortId')?.toString() ??
					foundCargo.CargoPorts[0]?.id?.toString();
				const newPath = `/contracts/${foundCargo.voyageId}/#/claims?cargoId=${cargoId}&cargoPortId=${cargoPortId}`;

				if (cargoPortId) {
					setSelectedCargoPortId(cargoPortId);
				}

				history.replace(newPath);
			}
		}

		if (!cargoId && cargos && cargos.length > 0) {
			const sortedCargos = [...cargos].sort(sortCargos);
			const firstCargo = sortedCargos[0];
			const firstCargoId = firstCargo.id.toString();
			const { voyageId } = firstCargo;
			const firstCargoPortId = firstCargo.CargoPorts[0].id.toString();
			const newPath = `/contracts/${voyageId}/#/claims?cargoId=${firstCargoId}&cargoPortId=${firstCargoPortId}`;
			history.replace(newPath);
			setSelectedCargoId(firstCargoId);
			setSelectedCargoPortId(firstCargoPortId);
		}
	}, [cargos, history, location.hash, selectedCargoId, selectedCargoPortId]);

	const tabs = useMemo(() => {
		if (!cargos) {
			return [];
		}

		return [...cargos]
			.sort(sortCargos)
			.map((c) => {
				const isVcIn = c.contractSide === CargoContractSides.VC_IN;

				return {
					key: c.id.toString(),
					label: (
						<CargoContractSideTag
							contractSide={isVcIn ? CargoContractSides.VC_IN : CargoContractSides.VC_OUT}
							chartererName={c.FixtureCounterparty.name}
						/>
					),
				};
			});
	}, [cargos]);

	const selectedCargo = useMemo(() => {
		if (!selectedCargoId) {
			return null;
		}

		const relevantCargo = cargos
			?.find((c) => c.id === Number(selectedCargoId));

		if (!relevantCargo) {
			return null;
		}

		return relevantCargo;
	}, [selectedCargoId, cargos]);

	const handleCargoTabChange = (cargoId: string) => {
		const cargo = cargos?.find((c) => c.id === Number(cargoId));

		if (!cargo) {
			return;
		}

		const cargoPortId = cargo.CargoPorts[0]?.id?.toString();
		setSelectedCargoId(cargoId);
		setSelectedCargoPortId(cargoPortId);
		history.replace(
			`/contracts/${cargo.voyageId}/#/claims?cargoId=${cargoId}&cargoPortId=${cargoPortId}`,
		);
	};

	if (!selectedCargo) {
		return null;
	}

	return (
		<Card
			activeTabKey={selectedCargoId}
			onTabChange={handleCargoTabChange}
			tabList={tabs}
		>
			<Flex vertical gap={20}>
				<LaytimeInfo
					key={selectedCargo?.id}
					selectedCargo={selectedCargo}
				/>
				<Claims
					selectedCargo={selectedCargo}
					selectedCargoPortId={selectedCargoPortId}
					setSelectedCargoPortId={setSelectedCargoPortId}
					enableNavigation
					enableExport
				/>
			</Flex>
		</Card>
	);
};

export default ClaimsTab;
